import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DateInput from '../forms/dateInput';
import { useQuery } from 'react-query';
import useCompanyMessaging from '../../hooks/useCompanyMessaging';
import { emailCustomerMessage, getJobTracking } from '../../services/tracking';
import { buildViberInvoiceUrl, buildWhatsAppInvoiceUrl } from '../../utils/invoiceMessaging';
import { customerStageLabel } from '../../constants/labels';
import ConfirmationModal from '../modals/confirmation';
import { toast } from '../../utils/toast';

/**
 * "Message the customer" prompt shown after a job reaches Confirmed,
 * Scheduled, Ready or Completed when tracking is enabled for the company.
 * Soroby prepares the message + tracking link; the employee presses Send —
 * nothing leaves without that press, and "Skip" closes it with no message.
 *
 * Props: { jobId, stage, client, onClose } — stage is the canonical job
 * status just reached; client = { firstName, lastName, phone, email } (may be
 * undefined); onClose MUST be called when the prompt is dismissed.
 */

// Company Profile keys the four stage templates in lower case.
const TEMPLATE_KEY = { CONFIRMED: 'confirmed', SCHEDULED: 'scheduled', READY: 'ready', COMPLETED: 'completed' };

const escapeRe = (s) => s.replace(/[{}]/g, '\\$&');
const fill = (template, values) => Object.entries(values).reduce(
    (text, [key, value]) => text.replace(new RegExp(escapeRe(`{${key}}`), 'g'), value ?? ''),
    String(template || ''),
);

// "2026-09-12" + "14:00 – 16:00" → "12.09.2026, 14:00 – 16:00" (date-only when no window).
const formatWindow = (date, window) => {
    if (!date) return '';
    const [y, m, d] = date.split('-');
    const nice = y && m && d ? `${d}.${m}.${y}` : date;
    return window?.trim() ? `${nice}, ${window.trim()}` : nice;
};

const CustomerMessagePrompt = ({ jobId, stage, client, onClose }) => {
    const { t } = useTranslation();
    const { trackingEnabled, trackingMessages, isLoaded } = useCompanyMessaging();
    const templateKey = TEMPLATE_KEY[String(stage || '').toUpperCase()];

    const { data, isLoading, isError, refetch } = useQuery(['job-tracking', jobId], () => getJobTracking(jobId), {
        enabled: !!jobId && trackingEnabled && !!templateKey,
        refetchOnWindowFocus: false,
    });
    const link = data?.code === 200 ? data.message : null;
    const linkError = isError || (data && data.code !== 200) ? (typeof data?.message === 'string' ? data.message : t('customerMessage.linkCouldNotBePrepared')) : '';

    const [message, setMessage] = useState('');
    const [messageEdited, setMessageEdited] = useState(false);
    const [recipient, setRecipient] = useState(client?.email || '');
    const [date, setDate] = useState('');
    const [window_, setWindow] = useState('');
    const [sending, setSending] = useState(false);
    const [shared, setShared] = useState('');
    const [copied, setCopied] = useState(false);
    const [confirmSend, setConfirmSend] = useState(false);


    const clientName = [client?.firstName, client?.lastName].filter(Boolean).join(' ') || t('jobs.client');
    const template = trackingMessages?.[templateKey] || '';
    const needsDate = String(stage).toUpperCase() === 'SCHEDULED' && template.includes('{date}');
    const dateText = useMemo(() => formatWindow(date, window_), [date, window_]);

    // Prefill from the company template; never overwrite text the user has
    // already edited. {link} is filled once the link is known.
    useEffect(() => {
        if (messageEdited) return;
        const filled = fill(template, {
            clientName,
            jobNumber: String(jobId),
            date: needsDate ? (dateText || '{date}') : dateText,
            link: link?.url || '{link}',
        });
        // The whole point of the message is the link, so it is never left to
        // the template: a company message saved without {link}, or read before
        // the link resolved, would otherwise go out with no way to track
        // anything. If the URL is known and absent, append it.
        const withLink = link?.url && !filled.includes(link.url)
            ? `${filled.replace(/\{link\}/g, '').trimEnd()}\n\n${link.url}`
            : filled;
        setMessage(withLink);
    }, [template, clientName, jobId, dateText, needsDate, link?.url, messageEdited]);

    // Tracking off (or a stage with no template) → nothing to prepare. Close
    // silently, and only once the settings are actually known.
    const nothingToDo = isLoaded && (!trackingEnabled || !templateKey || (link && link.enabled === false));
    useEffect(() => { if (nothingToDo) onClose(); }, [nothingToDo, onClose]);
    if (nothingToDo || !isLoaded) return null;

    const hasPhone = !!String(client?.phone || '').replace(/\D/g, '');
    const linkReady = !!link?.url;
    const dateMissing = needsDate && !date;
    const canSend = linkReady && !dateMissing && !sending;
    const stageLabel = customerStageLabel(stage);

    // Neither channel can confirm delivery: WhatsApp opens a tab, Viber hands
    // off to a viber:// URL that does nothing at all when the app is not
    // installed — which is every desktop without it. Closing the panel on the
    // click threw the prepared message away with no way to tell whether
    // anything happened, so the panel now stays open and says what to expect.
    const shareMessage = (channel) => {
        if (!canSend) return;
        if (channel === 'whatsapp') {
            window.open(buildWhatsAppInvoiceUrl(client?.phone, message), '_blank', 'noopener,noreferrer');
        } else {
            window.location.href = buildViberInvoiceUrl(client?.phone, message);
        }
        setShared(channel);
    };

    const copyMessage = async () => {
        try {
            await navigator.clipboard.writeText(message);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error(t('customerMessage.copyNotAvailable'));
        }
    };

    const sendEmail = async () => {
        setSending(true);
        try {
            const response = await emailCustomerMessage(jobId, { email: recipient, message });
            if (response?.code !== 200) throw new Error(typeof response?.message === 'string' ? response.message : t('documentShare.emailFailed'));
            toast.success(t('customerMessage.sentTo', { recipient }));
            setShared('email');
        } catch (error) {
            toast.error(error?.message || t('customerMessage.couldNotBeSent'));
        } finally {
            setSending(false);
        }
    };

    return <div className="qap-backdrop" onMouseDown={onClose}>
        <aside className="qap" onMouseDown={(event) => event.stopPropagation()} aria-label={t('customerMessage.title')}>
            <div className="qap-head">
                <div><b>{t('customerMessage.title')}</b><span>{stageLabel} · {t('jobs.jobNumber', { id: jobId })} · {clientName}</span></div>
                <button type="button" onClick={onClose} aria-label={t('common.close')}>×</button>
            </div>
            <div className="qap-form invoice">
                {needsDate &&
                    <>
                        <label>
                            {t('customerMessage.scheduledDate')}
                            <DateInput value={date} onChange={(event) => { setDate(event.target.value); }} />
                        </label>
                        <label>
                            {t('customerMessage.timeWindow')} <small className="qap-hint">({t('common.optional')}, {t('customerMessage.forExample', { example: '14:00 – 16:00' })})</small>
                            <input type="text" value={window_} onChange={(event) => setWindow(event.target.value)} placeholder="14:00 – 16:00" />
                        </label>
                        {dateMissing && <div className="qap-share-note neutral">{t('customerMessage.pickDateFirst')}</div>}
                    </>
                }
                <label>
                    {t('documentShare.recipientEmail')}
                    <input type="email" value={recipient} onChange={(event) => setRecipient(event.target.value)} placeholder="client@example.com" />
                    {!recipient && <small className="qap-hint">{t('documentShare.noEmailHint')}</small>}
                </label>
                <label>
                    {t('documentShare.message')}
                    <textarea rows="8" value={message} onChange={(event) => { setMessage(event.target.value); setMessageEdited(true); }} />
                    <small className="qap-hint">{t('customerMessage.prefillHint', { stageLabel })}</small>
                </label>
                {link?.url && !message.includes(link.url) &&
                    <div className="qap-share-note error">
                        {t('customerMessage.linkRemoved')}
                        <button type="button" onClick={() => setMessage(`${message.replace(/\{link\}/g, '').trimEnd()}\n\n${link.url}`)}>{t('customerMessage.addLink')}</button>
                    </div>
                }
                {isLoading && <div className="qap-share-note neutral">{t('customerMessage.preparingLink')}</div>}
                {linkError && <div className="qap-share-note error">{linkError} <button type="button" onClick={() => refetch()}>{t('documentShare.tryAgain')}</button></div>}
                {link?.url && <div className="qap-share-note neutral" style={{ overflowWrap: 'anywhere' }}>{t('customerMessage.trackingLink')}: {link.url}{link.expiresAt ? ` · ${t('trackingLink.expires', { date: new Date(link.expiresAt).toLocaleDateString() })}` : ''}</div>}
                {!hasPhone && <div className="qap-share-note neutral">{t('documentShare.noPhoneHint')}</div>}
                <div className="qap-send">
                    <button type="button" disabled={!canSend} onClick={() => shareMessage('whatsapp')}>WhatsApp</button>
                    <button type="button" disabled={!canSend} onClick={() => shareMessage('viber')}>Viber</button>
                    <button type="button" disabled={!canSend || !recipient} onClick={() => setConfirmSend(true)}>{sending ? t('documentShare.sending') : t('common.email')}</button>
                </div>
                {shared === 'viber' &&
                    <div className="qap-share-note neutral">
                        {t('customerMessage.viberOpened')}
                    </div>
                }
                {shared === 'whatsapp' &&
                    <div className="qap-share-note neutral">
                        {t('customerMessage.whatsappOpened')}
                    </div>
                }
                <div className="qap-send">
                    <button type="button" onClick={copyMessage} disabled={!linkReady}>{copied ? t('customerMessage.copied') : t('customerMessage.copyMessage')}</button>
                </div>
                <button type="button" className="qap-save" style={{ background: 'transparent', color: 'var(--muted)', border: '1px solid var(--line)' }} onClick={onClose} disabled={sending}>
                    {shared ? t('customerMessage.doneClose') : t('customerMessage.skip')}
                </button>
                <ConfirmationModal display={confirmSend} toggle={setConfirmSend} tone="primary"
                    title={t('customerMessage.confirmTitle')} confirmText={t('invoiceShare.send')} cancelText={t('common.cancel')}
                    message={t('customerMessage.confirmMessage', { recipient })}
                    confirmAction={sendEmail} />
            </div>
        </aside>
    </div>;
};

export default CustomerMessagePrompt;
